import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Image, Upload, Plus, Search, Filter, MoreVertical, Grid, List,
  Trash2, Download, Copy, CheckCircle, File, Film, Music, Folder,
  FolderPlus, ChevronRight, Home, Move, Edit2, X, Check, AlertCircle,
  FileWarning, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';

// Default upload limits (will be overridden by server config)
const DEFAULT_MAX_FILE_SIZE_MB = 10;
const DEFAULT_MAX_FILES_PER_UPLOAD = 50;
const DEFAULT_MAX_VIDEO_SIZE_MB = 50;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm'];
const ALLOWED_DOCUMENT_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/csv'];
const ALLOWED_TYPES = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES, ...ALLOWED_DOCUMENT_TYPES];

const typeConfig = {
  image: { label: 'Image', icon: Image, color: 'bg-blue-500' },
  video: { label: 'Video', icon: Film, color: 'bg-purple-500' },
  audio: { label: 'Audio', icon: Music, color: 'bg-amber-500' },
  document: { label: 'Document', icon: File, color: 'bg-emerald-500' },
};

/**
 * Derive the destination subfolder path for a file picked via a directory
 * input. A `webkitdirectory` picker sets `file.webkitRelativePath` to
 * something like "PickedFolder/sub/img.jpg"; we want "PickedFolder/sub" —
 * the folder tree to recreate in the media library. The backend's
 * `folder_path` param auto-creates that hierarchy under the current folder.
 *
 * Returns '' for a file with no relative path (i.e. an ordinary
 * multi-file selection), so callers can treat both flows uniformly.
 * Exported for unit testing.
 */
export function relativeFolderPath(file) {
  const rel = (file && file.webkitRelativePath) || '';
  if (!rel.includes('/')) return '';
  const parts = rel.split('/');
  parts.pop(); // drop the filename, keep the folder chain
  return parts.filter(Boolean).join('/');
}

export default function Media() {
  const [mediaItems, setMediaItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  
  // Upload configuration (fetched from server)
  const [uploadConfig, setUploadConfig] = useState({
    maxFileSizeMb: DEFAULT_MAX_FILE_SIZE_MB,
    maxVideoSizeMb: DEFAULT_MAX_VIDEO_SIZE_MB,
    maxFilesPerBatch: DEFAULT_MAX_FILES_PER_UPLOAD
  });
  
  // Current folder navigation
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  
  // Dialogs
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showUploadLogDialog, setShowUploadLogDialog] = useState(false);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, percent: 0 });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadFolder, setUploadFolder] = useState(null);
  const [optimizeForWeb, setOptimizeForWeb] = useState(true);
  const [outputFormat, setOutputFormat] = useState("webp");
  const [uploadLog, setUploadLog] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  
  // Selection state
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  
  // New folder/rename state
  const [newFolderName, setNewFolderName] = useState('');
  const [renameItem, setRenameItem] = useState(null);
  const [renameName, setRenameName] = useState('');
  
  // Refs
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // `webkitdirectory` is what turns the hidden input into a *folder* picker
  // (otherwise it's an ordinary file picker that opens folders instead of
  // selecting them). It isn't a standard React attribute, so we set it on
  // the DOM node directly via a ref callback. A callback (not a useEffect)
  // is essential here: the input lives inside the upload dialog, which is
  // only mounted when the dialog is open, so the node mounts/unmounts each
  // time. The callback fires on every mount and re-applies the attribute;
  // a one-shot useEffect([]) would run while the dialog is still closed
  // (node === null) and never apply it.
  const setFolderInput = (node) => {
    folderInputRef.current = node;
    if (node) {
      node.setAttribute('webkitdirectory', '');
      node.setAttribute('directory', '');     // Firefox
      node.setAttribute('mozdirectory', '');   // older Firefox
    }
  };

  useEffect(() => {
    loadContent();
  }, [currentFolder, typeFilter]);

  // Fetch upload configuration from server
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await apiV1.get('/media/config');
        if (response.data.success) {
          setUploadConfig({
            maxFileSizeMb: response.data.data.max_file_size_mb,
            maxVideoSizeMb: response.data.data.max_video_size_mb || DEFAULT_MAX_VIDEO_SIZE_MB,
            maxFilesPerBatch: response.data.data.max_files_per_batch
          });
        }
      } catch (error) {
        console.warn('Could not fetch upload config, using defaults');
      }
    };
    fetchConfig();
  }, []);

  // Computed values from config
  const maxSizeFor = (file) => (
    ALLOWED_VIDEO_TYPES.includes(file.type) ? uploadConfig.maxVideoSizeMb : uploadConfig.maxFileSizeMb
  ) * 1024 * 1024;
  const maxFilesPerUpload = uploadConfig.maxFilesPerBatch;

  const loadContent = async () => {
    setLoading(true);
    try {
      // Load folders
      const foldersRes = await apiV1.get('/media/folders', {
        params: { parent_id: currentFolder }
      });
      if (foldersRes.data.success) {
        setFolders(foldersRes.data.data);
      }
      
      // Load media files
      const params = { folder: currentFolder || '' };
      if (typeFilter) params.media_type = typeFilter;
      
      const mediaRes = await apiV1.get('/media/', { params });
      if (mediaRes.data.success) {
        // Filter files without folder when at root, or with matching folder
        const files = mediaRes.data.data.filter(f => 
          currentFolder ? f.folder === currentFolder : !f.folder
        );
        setMediaItems(files);
      }
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = (folder) => {
    if (folder) {
      setFolderPath([...folderPath, { id: folder.id, name: folder.name }]);
      setCurrentFolder(folder.id);
    } else {
      setFolderPath([]);
      setCurrentFolder(null);
    }
    setSelectedItems([]);
  };

  const navigateToBreadcrumb = (index) => {
    if (index === -1) {
      // Home
      setFolderPath([]);
      setCurrentFolder(null);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setFolderPath(newPath);
      setCurrentFolder(newPath[newPath.length - 1]?.id || null);
    }
    setSelectedItems([]);
  };

  // Validate a single file before adding to selection
  const validateFile = (file) => {
    const errors = [];
    
    // Check file size (videos have their own limit)
    const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type);
    const limitMb = isVideo ? uploadConfig.maxVideoSizeMb : uploadConfig.maxFileSizeMb;
    if (file.size > limitMb * 1024 * 1024) {
      errors.push(`File too large (${formatFileSize(file.size)}). Maximum size is ${limitMb}MB${isVideo ? ' for videos' : ''}.`);
    }
    
    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      errors.push(`Unsupported file type: ${file.type || 'unknown'}. Allowed: images, videos, PDF, DOC, CSV.`);
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  };

  // Process files from input or drag/drop.
  // `allowLargeBatch` (folder uploads) bypasses the per-batch truncation —
  // a directory can hold hundreds of files across subfolders — and silently
  // drops OS junk / hidden dotfiles (.DS_Store, Thumbs.db, etc.) so they
  // don't flood the skip log.
  const processFiles = (fileList, { allowLargeBatch = false } = {}) => {
    let files = Array.from(fileList);
    const validFiles = [];
    const skippedFiles = [];

    if (allowLargeBatch) {
      files = files.filter(f => f.name && !f.name.startsWith('.'));
    } else if (files.length > maxFilesPerUpload) {
      toast.warning(`Maximum ${maxFilesPerUpload} files per upload. ${files.length - maxFilesPerUpload} files will be skipped.`);
    }

    const filesToProcess = allowLargeBatch ? files : files.slice(0, maxFilesPerUpload);
    
    for (const file of filesToProcess) {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        skippedFiles.push({
          name: file.name,
          size: file.size,
          errors: validation.errors
        });
      }
    }
    
    // Show skipped files warning
    if (skippedFiles.length > 0) {
      const skippedNames = skippedFiles.slice(0, 3).map(f => f.name).join(', ');
      const moreCount = skippedFiles.length > 3 ? ` +${skippedFiles.length - 3} more` : '';
      toast.error(`${skippedFiles.length} file(s) skipped: ${skippedNames}${moreCount}`, {
        duration: 5000,
        description: 'Check upload log for details'
      });
      
      // Add to upload log
      skippedFiles.forEach(f => {
        addToUploadLog(f.name, 'skipped', f.errors.join('; '));
      });
    }
    
    return validFiles;
  };

  const handleFileSelect = (e) => {
    const validFiles = processFiles(e.target.files);
    setSelectedFiles(prev => [...prev, ...validFiles]);
    // Reset input so same file can be selected again
    if (e.target) e.target.value = '';
  };

  // Folder picker: every file carries a `webkitRelativePath`, so the
  // subfolder tree is preserved on upload (see relativeFolderPath +
  // handleUpload). No per-batch truncation; junk files are dropped.
  const handleFolderSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    const validFiles = processFiles(picked, { allowLargeBatch: true });
    setSelectedFiles(prev => [...prev, ...validFiles]);
    if (validFiles.length > 0) {
      const topFolder = (validFiles[0].webkitRelativePath || '').split('/')[0];
      toast.info(
        `Added ${validFiles.length} file(s)${topFolder ? ` from "${topFolder}"` : ''}. ` +
        `Subfolders will be recreated in the library.`
      );
    }
    if (e.target) e.target.value = '';
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if leaving the drop zone entirely
    if (e.currentTarget && !e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = e.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      const validFiles = processFiles(droppedFiles);
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  // Upload log helpers
  const addToUploadLog = (filename, status, message = '') => {
    const entry = {
      id: Date.now() + Math.random(),
      filename,
      status, // 'pending', 'uploading', 'success', 'error', 'skipped'
      message,
      timestamp: new Date().toISOString()
    };
    setUploadLog(prev => [entry, ...prev].slice(0, 100)); // Keep last 100 entries
  };

  const updateUploadLog = (id, status, message = '') => {
    setUploadLog(prev => prev.map(entry => 
      entry.id === id ? { ...entry, status, message } : entry
    ));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files');
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: selectedFiles.length, percent: 0 });
    
    const results = {
      success: 0,
      failed: 0,
      errors: []
    };

    // Create log entries for all files
    const logEntries = selectedFiles.map(file => ({
      id: Date.now() + Math.random(),
      filename: file.name,
      fileSize: file.size,
      status: 'pending',
      message: 'Waiting...',
      timestamp: new Date().toISOString()
    }));
    setUploadLog(prev => [...logEntries, ...prev].slice(0, 100));

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const logEntry = logEntries[i];
      
      // Update progress
      const percent = Math.round(((i + 1) / selectedFiles.length) * 100);
      setUploadProgress({ current: i + 1, total: selectedFiles.length, percent });
      
      // Update log to uploading
      setUploadLog(prev => prev.map(entry => 
        entry.id === logEntry.id ? { ...entry, status: 'uploading', message: 'Uploading...' } : entry
      ));
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (uploadFolder || currentFolder) {
          formData.append('folder', uploadFolder || currentFolder);
        }
        // For folder uploads, send the file's subfolder path so the backend
        // recreates the tree (nested under the current folder if any).
        const subfolderPath = relativeFolderPath(file);
        if (subfolderPath) {
          formData.append('folder_path', subfolderPath);
        }
        formData.append('optimize_for_web', optimizeForWeb.toString());
        formData.append('output_format', outputFormat);
        
        const response = await apiV1.post('/media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000 // 60 second timeout for large files
        });
        
        if (response.data.success) {
          results.success++;
          setUploadLog(prev => prev.map(entry => 
            entry.id === logEntry.id ? { 
              ...entry, 
              status: 'success', 
              message: `Uploaded as ${response.data.data.filename}${optimizeForWeb ? ' (optimized)' : ''}`,
              resultUrl: response.data.data.url
            } : entry
          ));
        } else {
          results.failed++;
          const errorMsg = response.data.detail || 'Unknown error';
          results.errors.push({ filename: file.name, error: errorMsg });
          setUploadLog(prev => prev.map(entry => 
            entry.id === logEntry.id ? { ...entry, status: 'error', message: errorMsg } : entry
          ));
        }
      } catch (error) {
        results.failed++;
        let errorMsg = 'Upload failed';
        
        // Extract the most detailed error message available
        // The error is transformed by the axios interceptor, check multiple sources
        if (error.detail) {
          // Direct detail from transformed error
          errorMsg = error.detail;
        } else if (error.message && error.message !== 'An error occurred') {
          // Transformed message (if not generic)
          errorMsg = error.message;
        } else if (error.response?.data?.detail) {
          // Raw response detail (fallback)
          errorMsg = error.response.data.detail;
        } else if (error.status) {
          // HTTP status code based messages
          const statusMessages = {
            400: 'Bad request - file may be invalid or corrupted',
            401: 'Session expired - please log in again',
            403: 'Permission denied - you may not have upload rights',
            404: 'Upload service not available',
            413: 'File too large - exceeds server limit',
            415: 'Unsupported file type',
            422: 'Invalid file - could not process',
            500: 'Server error during upload',
            502: 'Server temporarily unavailable',
            503: 'Service overloaded - try again later'
          };
          errorMsg = statusMessages[error.status] || `Upload failed (error ${error.status})`;
        } else if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
          errorMsg = 'Network error - check your internet connection';
        } else if (error.code === 'ECONNABORTED') {
          errorMsg = 'Upload timed out - connection too slow or file too large';
        }
        
        results.errors.push({ filename: file.name, error: errorMsg });
        setUploadLog(prev => prev.map(entry => 
          entry.id === logEntry.id ? { ...entry, status: 'error', message: errorMsg } : entry
        ));
      }
    }

    setUploading(false);
    setUploadProgress({ current: 0, total: 0, percent: 0 });
    setShowUploadDialog(false);
    setSelectedFiles([]);
    setUploadFolder(null);
    
    // Show results
    if (results.success > 0 && results.failed === 0) {
      toast.success(`Uploaded ${results.success} file(s) successfully${optimizeForWeb ? ' (optimized)' : ''}`);
    } else if (results.success > 0 && results.failed > 0) {
      toast.warning(`Uploaded ${results.success} file(s), ${results.failed} failed`, {
        description: 'Check upload log for details',
        action: {
          label: 'View Log',
          onClick: () => setShowUploadLogDialog(true)
        }
      });
    } else if (results.failed > 0) {
      toast.error(`All ${results.failed} upload(s) failed`, {
        description: 'Check upload log for details',
        action: {
          label: 'View Log',
          onClick: () => setShowUploadLogDialog(true)
        }
      });
    }
    
    loadContent();
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      const result = await apiV1.post('/media/folders', null, {
        params: { name: newFolderName, parent_id: currentFolder }
      });
      
      if (result.data.success) {
        toast.success('Folder created');
        setShowNewFolderDialog(false);
        setNewFolderName('');
        loadContent();
      }
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const handleRenameFolder = async () => {
    if (!renameName.trim() || !renameItem) return;

    try {
      const result = await apiV1.put(`/media/folders/${renameItem.id}`, null, {
        params: { name: renameName }
      });
      
      if (result.data.success) {
        toast.success('Folder renamed');
        setShowRenameDialog(false);
        setRenameItem(null);
        setRenameName('');
        loadContent();
      }
    } catch (error) {
      toast.error('Failed to rename folder');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Files will be moved to the root folder.')) return;
    
    try {
      await apiV1.delete(`/media/folders/${folderId}`);
      toast.success('Folder deleted');
      loadContent();
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  const handleDeleteFile = async (id) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      await apiV1.delete(`/media/${id}`);
      toast.success('File deleted');
      loadContent();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleMoveItems = async (targetFolderId) => {
    try {
      const result = await apiV1.post('/media/move', {
        file_ids: selectedItems,
        folder_id: targetFolderId || null
      });
      
      if (result.data.success) {
        toast.success(`Moved ${result.data.data.moved_count} file(s)`);
        setShowMoveDialog(false);
        setSelectedItems([]);
        setIsSelectionMode(false);
        loadContent();
      }
    } catch (error) {
      toast.error('Failed to move files');
    }
  };

  const handleCopyUrl = (url) => {
    const fullUrl = window.location.origin + url;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copied to clipboard');
  };

  const toggleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedItems(mediaItems.map(m => m.id));
  };

  const deselectAll = () => {
    setSelectedItems([]);
  };

  const filteredMedia = mediaItems.filter(item =>
    item.filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.original_filename?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTypeIcon = (type) => {
    const config = typeConfig[type] || typeConfig.document;
    const Icon = config.icon;
    return <Icon className="w-4 h-4" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6" data-testid="media-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Media Library</h1>
          <p className="text-muted-foreground mt-1">Manage your images and files</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowNewFolderDialog(true)} data-testid="new-folder-btn">
            <FolderPlus className="w-4 h-4 mr-2" />
            New Folder
          </Button>
          <Button onClick={() => setShowUploadDialog(true)} data-testid="upload-media-btn">
            <Upload className="w-4 h-4 mr-2" />
            Upload Files
          </Button>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      <div className="flex items-center gap-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink 
                onClick={() => navigateToBreadcrumb(-1)}
                className="cursor-pointer flex items-center gap-1 hover:text-primary"
              >
                <Home className="w-4 h-4" />
                Media Library
              </BreadcrumbLink>
            </BreadcrumbItem>
            {folderPath.map((folder, index) => (
              <React.Fragment key={folder.id}>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink 
                    onClick={() => navigateToBreadcrumb(index)}
                    className="cursor-pointer hover:text-primary"
                  >
                    {folder.name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={typeFilter || "all"} onValueChange={(val) => setTypeFilter(val === "all" ? "" : val)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="image">Images</SelectItem>
              <SelectItem value="video">Videos</SelectItem>
              <SelectItem value="document">Documents</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          {selectedItems.length > 0 && (
            <>
              <Badge variant="secondary">{selectedItems.length} selected</Badge>
              <Button variant="outline" size="sm" onClick={() => setShowMoveDialog(true)}>
                <Move className="w-4 h-4 mr-2" />
                Move
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAll}>
                <X className="w-4 h-4 mr-2" />
                Deselect
              </Button>
            </>
          )}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
          >
            {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[...Array(12)].map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-lg" />
          ))}
        </div>
      ) : (
        <div className={viewMode === 'grid' 
          ? "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" 
          : "space-y-2"
        }>
          {/* Folders */}
          {folders.map(folder => (
            <div
              key={folder.id}
              className={viewMode === 'grid'
                ? "group relative aspect-square bg-muted rounded-lg border hover:border-primary cursor-pointer transition-colors"
                : "flex items-center gap-4 p-3 bg-muted rounded-lg border hover:border-primary cursor-pointer transition-colors"
              }
              onDoubleClick={() => navigateToFolder(folder)}
            >
              <div className={viewMode === 'grid' 
                ? "flex flex-col items-center justify-center h-full p-4"
                : "flex items-center gap-3 flex-1"
              }>
                <Folder className={viewMode === 'grid' ? "w-16 h-16 text-amber-500" : "w-8 h-8 text-amber-500"} />
                <span className="font-medium text-sm truncate">{folder.name}</span>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className={viewMode === 'grid' 
                      ? "absolute top-2 right-2 opacity-0 group-hover:opacity-100"
                      : ""
                    }
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigateToFolder(folder); }}>
                    <Folder className="w-4 h-4 mr-2" />
                    Open
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { 
                    e.stopPropagation();
                    setRenameItem(folder);
                    setRenameName(folder.name);
                    setShowRenameDialog(true);
                  }}>
                    <Edit2 className="w-4 h-4 mr-2" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="text-destructive"
                    onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}

          {/* Files */}
          {filteredMedia.map(item => (
            <div
              key={item.id}
              className={`group relative ${
                viewMode === 'grid'
                  ? "aspect-square bg-muted rounded-lg border overflow-hidden"
                  : "flex items-center gap-4 p-3 bg-muted rounded-lg border"
              } ${selectedItems.includes(item.id) ? 'ring-2 ring-primary' : ''} hover:border-primary transition-colors`}
            >
              {/* Checkbox */}
              <div 
                className={viewMode === 'grid' 
                  ? "absolute top-2 left-2 z-10"
                  : "flex-shrink-0"
                }
              >
                <Checkbox
                  checked={selectedItems.includes(item.id)}
                  onCheckedChange={() => toggleSelectItem(item.id)}
                  className="bg-background"
                />
              </div>

              {viewMode === 'grid' ? (
                <>
                  {item.media_type === 'image' ? (
                    <img
                      src={item.url}
                      alt={item.original_filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      {getTypeIcon(item.media_type)}
                      <span className="sr-only">{item.original_filename}</span>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-xs truncate">{item.original_filename}</p>
                    <p className="text-white/70 text-xs">{formatFileSize(item.file_size)}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 flex-shrink-0 bg-background rounded flex items-center justify-center overflow-hidden">
                    {item.media_type === 'image' ? (
                      <img src={item.url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      getTypeIcon(item.media_type)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.original_filename}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatFileSize(item.file_size)}
                      {item.width && item.height && ` • ${item.width}×${item.height}`}
                    </p>
                  </div>
                </>
              )}

              {/* Actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className={viewMode === 'grid' 
                      ? "absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-background/80"
                      : ""
                    }
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => handleCopyUrl(item.url)}>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy URL
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => window.open(item.url, '_blank')}>
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    setSelectedItems([item.id]);
                    setShowMoveDialog(true);
                  }}>
                    <Move className="w-4 h-4 mr-2" />
                    Move to Folder
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteFile(item.id)}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}

          {/* Empty State */}
          {folders.length === 0 && filteredMedia.length === 0 && (
            <div className="col-span-full text-center py-12">
              <Folder className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium">No files here</h3>
              <p className="text-muted-foreground mb-4">Upload files or create a folder to get started</p>
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={() => setShowNewFolderDialog(true)}>
                  <FolderPlus className="w-4 h-4 mr-2" />
                  New Folder
                </Button>
                <Button onClick={() => setShowUploadDialog(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Files
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => {
        setShowUploadDialog(open);
        if (!open) {
          setSelectedFiles([]);
          setIsDragging(false);
        }
      }}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Files
            </DialogTitle>
            <DialogDescription>
              Upload images, videos, or documents to your media library
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Upload limits info */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded">
              <span>Max file size: <strong>{uploadConfig.maxFileSizeMb}MB</strong> (videos <strong>{uploadConfig.maxVideoSizeMb}MB</strong>)</span>
              <span>•</span>
              <span>Max files: <strong>{uploadConfig.maxFilesPerBatch}</strong></span>
              <span>•</span>
              <Button 
                variant="link" 
                size="sm" 
                className="h-auto p-0 text-xs"
                onClick={() => setShowUploadLogDialog(true)}
              >
                <Clock className="w-3 h-3 mr-1" />
                View Upload Log
              </Button>
            </div>
            
            {/* Drag and drop zone */}
            <div 
              ref={dropZoneRef}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer
                ${isDragging 
                  ? 'border-primary bg-primary/10 scale-[1.02]' 
                  : 'hover:border-primary hover:bg-muted/50'
                }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,video/*,application/pdf,.doc,.docx,.csv"
                data-testid="file-upload-input"
              />
              
              {isDragging ? (
                <>
                  <Upload className="w-12 h-12 mx-auto text-primary mb-3 animate-bounce" />
                  <p className="text-base font-medium text-primary">
                    Drop files here!
                  </p>
                </>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">
                    Drag & drop files here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Images (JPG, PNG, GIF, WebP), Videos (MP4), Documents (PDF, DOC)
                  </p>
                </>
              )}
            </div>

            {/* Upload an entire folder (preserves subfolder structure) */}
            <div className="flex items-center justify-center gap-2 -mt-1">
              <span className="text-xs text-muted-foreground">or</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => folderInputRef.current?.click()}
                data-testid="folder-upload-button"
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                Upload a folder
              </Button>
              <span className="text-xs text-muted-foreground">
                (subfolders are recreated in the library)
              </span>
              {/* webkitdirectory is applied by the setFolderInput ref callback */}
              <input
                ref={setFolderInput}
                type="file"
                multiple
                onChange={handleFolderSelect}
                className="hidden"
                data-testid="folder-upload-input"
              />
            </div>

            {/* Selected files list */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    Selected files 
                    <Badge variant="secondary">{selectedFiles.length}</Badge>
                  </Label>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedFiles([])}
                    className="text-xs h-7"
                  >
                    Clear all
                  </Button>
                </div>
                <ScrollArea className="h-[160px] border rounded-md">
                  <div className="p-2 space-y-1">
                    {selectedFiles.map((file, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm bg-muted p-2 rounded group">
                        {file.type?.startsWith('image/') ? (
                          <Image className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        ) : file.type?.startsWith('video/') ? (
                          <Film className="w-4 h-4 text-purple-500 flex-shrink-0" />
                        ) : (
                          <File className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        )}
                        <span className="truncate flex-1 min-w-0">{file.name}</span>
                        <span className="text-muted-foreground text-xs flex-shrink-0">{formatFileSize(file.size)}</span>
                        {file.size > maxSizeFor(file) && (
                          <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" title="File too large" />
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFiles(prev => prev.filter((_, idx) => idx !== i));
                          }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Total: {formatFileSize(selectedFiles.reduce((acc, f) => acc + f.size, 0))}</span>
                  {selectedFiles.length >= maxFilesPerUpload && (
                    <span className="text-amber-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Max files reached
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Upload progress */}
            {uploading && (
              <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span>Uploading {uploadProgress.current} of {uploadProgress.total}...</span>
                  <span className="font-medium">{uploadProgress.percent}%</span>
                </div>
                <Progress value={uploadProgress.percent} className="h-2" />
              </div>
            )}
            
            {/* Optimize for web option */}
            <div className="space-y-3 p-3 rounded-lg bg-muted/50">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="optimize-web"
                  checked={optimizeForWeb}
                  onCheckedChange={setOptimizeForWeb}
                  data-testid="optimize-checkbox"
                />
                <div className="space-y-1">
                  <Label htmlFor="optimize-web" className="cursor-pointer font-medium">
                    Optimize for web
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Resize large images to Full HD (1920×1080 max) while maintaining quality and aspect ratio.
                  </p>
                </div>
              </div>
              
              {optimizeForWeb && (
                <div className="ml-7 space-y-2">
                  <Label className="text-sm">Output format</Label>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="outputFormat"
                        value="webp"
                        checked={outputFormat === "webp"}
                        onChange={(e) => setOutputFormat(e.target.value)}
                        className="w-4 h-4 text-primary"
                      />
                      <span className="text-sm">
                        WebP <span className="text-xs text-muted-foreground">(best compression, ~30% smaller)</span>
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="outputFormat"
                        value="jpeg"
                        checked={outputFormat === "jpeg"}
                        onChange={(e) => setOutputFormat(e.target.value)}
                        className="w-4 h-4 text-primary"
                      />
                      <span className="text-sm">
                        JPEG <span className="text-xs text-muted-foreground">(good compression)</span>
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="outputFormat"
                        value="original"
                        checked={outputFormat === "original"}
                        onChange={(e) => setOutputFormat(e.target.value)}
                        className="w-4 h-4 text-primary"
                      />
                      <span className="text-sm">
                        Original format <span className="text-xs text-muted-foreground">(max compatibility, resize only)</span>
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </div>
            
            {folders.length > 0 && (
              <div className="space-y-2">
                <Label>Upload to folder (optional)</Label>
                <Select value={uploadFolder || "root"} onValueChange={(val) => setUploadFolder(val === "root" ? null : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Root folder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="root">Root folder</SelectItem>
                    {folders.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploading || selectedFiles.length === 0}
              data-testid="upload-submit-btn"
            >
              {uploading 
                ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...` 
                : `Upload ${selectedFiles.length} file(s)`
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolderDialog} onOpenChange={setShowNewFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Folder name</Label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Enter folder name"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewFolderDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder}>Create Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New name</Label>
              <Input
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                placeholder="Enter new name"
                onKeyDown={(e) => e.key === 'Enter' && handleRenameFolder()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenameDialog(false)}>Cancel</Button>
            <Button onClick={handleRenameFolder}>Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move to Folder</DialogTitle>
            <DialogDescription>
              Select a destination folder for {selectedItems.length} file(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleMoveItems(null)}
            >
              <Home className="w-4 h-4 mr-2" />
              Root folder
            </Button>
            {folders.map(folder => (
              <Button
                key={folder.id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleMoveItems(folder.id)}
              >
                <Folder className="w-4 h-4 mr-2 text-amber-500" />
                {folder.name}
              </Button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoveDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Log Dialog */}
      <Dialog open={showUploadLogDialog} onOpenChange={setShowUploadLogDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Upload Log
            </DialogTitle>
            <DialogDescription>
              Recent upload activity and status
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px] pr-4">
            {uploadLog.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No upload activity yet</p>
                <p className="text-sm">Upload some files to see the log</p>
              </div>
            ) : (
              <div className="space-y-2">
                {uploadLog.map((entry) => (
                  <div
                    key={entry.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      entry.status === 'success' ? 'bg-emerald-500/10 border-emerald-500/20' :
                      entry.status === 'error' ? 'bg-destructive/10 border-destructive/20' :
                      entry.status === 'skipped' ? 'bg-amber-500/10 border-amber-500/20' :
                      entry.status === 'uploading' ? 'bg-blue-500/10 border-blue-500/20' :
                      'bg-muted/50 border-border'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {entry.status === 'success' && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      {entry.status === 'error' && <AlertCircle className="w-4 h-4 text-destructive" />}
                      {entry.status === 'skipped' && <FileWarning className="w-4 h-4 text-amber-500" />}
                      {entry.status === 'uploading' && <Upload className="w-4 h-4 text-blue-500 animate-pulse" />}
                      {entry.status === 'pending' && <Clock className="w-4 h-4 text-muted-foreground" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{entry.filename}</p>
                      <p className="text-xs text-muted-foreground">{entry.message}</p>
                      {entry.fileSize && (
                        <p className="text-xs text-muted-foreground">{formatFileSize(entry.fileSize)}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-xs text-muted-foreground">
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          <DialogFooter className="flex-row justify-between sm:justify-between">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setUploadLog([])}
              disabled={uploadLog.length === 0}
            >
              Clear Log
            </Button>
            <Button onClick={() => setShowUploadLogDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
